import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfiguration, Tick, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "student-activities-balance-by-activity-chart",
    templateUrl: "./balanceByActivityChart.tmpl.html",
    standalone: false
})
export class BalanceByActivityChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    readonly ChartTitle: string = "Student Balance By Activity";
    isLoaded: boolean;
    constructor(
        @Inject(LOCALE_ID)
        private readonly locale: string,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService,
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService
    )
    {
        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "";
        const fiscal = this.chartCriteria?.Parameters?.find(x => x.Id === "SABalanceFiscal");
        if (fiscal?.Value)
        {
            const selectedFiscal = fiscal.Options.find(x => x.Value === fiscal.Value.toString());
            if (selectedFiscal)
            {
                ret += selectedFiscal.Display;
            }
        }
        return ret;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number, index: number, ticks: Tick[]): string | string[] | number | number[] | null | undefined =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        type: "bar",
                        label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: [
                            rgb(0, 143, 140),
                            rgb(13, 38, 68),
                            rgb(227, 96, 57),
                            rgb(92, 65, 112),
                            rgb(92, 110, 131),
                            rgb(100, 19, 19),
                            rgb(144, 137, 57),
                            rgb(36, 87, 24),
                            rgb(198, 61, 61),
                            rgb(0, 61, 60),
                        ],
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
